import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { authApi, needsSecondFactor } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { solvePow } from '@/lib/pow/solve'

import { useLogin, useLoginTwoFactor, useRegister } from '../hooks/useCurrentUser'

export function Field({
  label,
  error,
  ...input
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  const id = `field-${input.name}`
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink-muted">
        {label}
      </label>
      <input
        id={id}
        {...input}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn(
          'w-full rounded-md border bg-surface px-3 py-3 text-[15px] outline-none',
          'focus:border-brand-ink',
          error ? 'border-danger' : 'border-line-strong',
        )}
      />
      {error && (
        <p id={`${id}-error`} className="mt-1 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * 登录 / 注册完成后回到哪里。只接受站内路径 —— 以 / 开头、不是 //（那是协议相对的外站地址）。
 * 认领 agent 的链接会带着它来：没账号的人先注册，注册完要能回到认领页。
 */
function useNext() {
  const [params] = useSearchParams()
  const next = params.get('next') ?? ''
  const safe = next.startsWith('/') && !next.startsWith('//') ? next : ''
  return { next: safe || '/', query: safe ? `?next=${encodeURIComponent(safe)}` : '' }
}

export function LoginForm() {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  // 开了两步验证：密码对了之后拿到的 challenge（D89）
  const [challenge, setChallenge] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const login = useLogin()
  const toast = useToast()
  const navigate = useNavigate()
  const { next, query } = useNext()
  const t = useT()

  const error = login.error instanceof ApiError ? login.error : null

  const done = (reactivated: boolean) => {
    if (reactivated) toast.show(t('auth.welcomeBack'))
    navigate(next, { replace: true })
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    setExpired(false)
    login.mutate(
      { identifier: identifier.trim(), password },
      {
        onSuccess: (result) => {
          if (needsSecondFactor(result)) setChallenge(result.challenge)
          else done(result.reactivated)
        },
      },
    )
  }

  if (challenge) {
    return (
      <SecondFactorStep
        challenge={challenge}
        onDone={done}
        onBack={(timedOut) => {
          setChallenge(null)
          setPassword('')
          setExpired(timedOut)
        }}
      />
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h1 className="text-3xl font-extrabold">{t('auth.login')}</h1>

      <Field
        label={t('auth.identifier')}
        name="identifier"
        autoComplete="username"
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        required
      />
      <Field
        label={t('auth.password')}
        name="password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />

      {expired && !error && (
        <p role="alert" className="text-sm text-danger">
          {t('twoFactor.expired')}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {/* 封禁只在密码正确时才会告诉对方（D66），这句话要能被读懂，不用服务端的原文 */}
          {error.code === 'account_suspended' ? t('auth.suspended') : error.message}
        </p>
      )}

      <Button type="submit" size="lg" fullWidth loading={login.isPending}>
        {t('auth.login')}
      </Button>

      <p className="text-center text-[15px]">
        <Link to="/forgot-password" className="text-brand-ink hover:underline">
          {t('auth.forgotPassword')}
        </Link>
      </p>

      <p className="text-center text-[15px] text-ink-muted">
        {t('auth.noAccount')}{' '}
        <Link to={`/register${query}`} className="text-brand-ink hover:underline">
          {t('auth.register')}
        </Link>
      </p>
    </form>
  )
}

/**
 * 登录第二步：验证器里的 6 位数字，或者一个恢复码。
 *
 * challenge 5 分钟过期，改了密码也会失效 —— 那时退回第一步重新输密码，而不是停在这里一直报错。
 */
function SecondFactorStep({
  challenge,
  onDone,
  onBack,
}: {
  challenge: string
  onDone: (reactivated: boolean) => void
  onBack: (expired: boolean) => void
}) {
  const t = useT()
  const verify = useLoginTwoFactor()
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const error = verify.error instanceof ApiError ? verify.error : null

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    verify.mutate(
      { challenge, code: code.trim() },
      {
        onSuccess: ({ reactivated }) => onDone(reactivated),
        onError: (failure) => {
          if (failure instanceof ApiError && failure.code === 'challenge_expired') onBack(true)
        },
      },
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h1 className="text-3xl font-extrabold">{t('twoFactor.loginTitle')}</h1>
      <p className="text-[15px] text-ink-muted">
        {useRecovery ? t('twoFactor.loginRecoveryHint') : t('twoFactor.loginHint')}
      </p>
      <Field
        key={useRecovery ? 'recovery' : 'totp'}
        label={useRecovery ? t('twoFactor.recoveryCode') : t('twoFactor.code')}
        name="code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoComplete="one-time-code"
        inputMode={useRecovery ? 'text' : 'numeric'}
        maxLength={useRecovery ? 16 : 7}
        autoFocus
        required
      />
      {error && error.code !== 'challenge_expired' && (
        <p role="alert" className="text-sm text-danger">
          {error.code === 'invalid_code' ? t('twoFactor.wrongCode') : error.message}
        </p>
      )}
      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={verify.isPending}
        disabled={code.trim().length === 0}
      >
        {t('twoFactor.verify')}
      </Button>
      <div className="flex justify-between text-[15px]">
        <button
          type="button"
          className="text-brand-ink hover:underline"
          onClick={() => {
            setUseRecovery((value) => !value)
            setCode('')
          }}
        >
          {useRecovery ? t('twoFactor.useApp') : t('twoFactor.useRecovery')}
        </button>
        <button
          type="button"
          className="text-ink-muted hover:underline"
          onClick={() => onBack(false)}
        >
          {t('common.back')}
        </button>
      </div>
    </form>
  )
}

type PowSolution = { challenge: string; nonce: string }

/**
 * 注册前的工作量证明（D95）：打开注册页就开始在后台算，填完表通常已经算好。
 * 一道题只能用一次 —— 交出去之后立刻开始准备下一道，以备这次注册失败（比如用户名被占了）。
 */
function usePowSolution() {
  const pending = useRef<Promise<PowSolution> | null>(null)
  const prepare = useCallback(() => {
    const next = authApi
      .powChallenge()
      .then(async (c) => ({ challenge: c.challenge, nonce: await solvePow(c.salt, c.difficulty) }))
    next.catch(() => {}) // 失败留到 take() 时再报
    pending.current = next
    return next
  }, [])
  useEffect(() => {
    void prepare()
  }, [prepare])
  const take = useCallback(() => {
    const current = pending.current ?? prepare()
    pending.current = null
    return current
  }, [prepare])
  return { take, prepare }
}

export function RegisterForm() {
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [checking, setChecking] = useState(false)
  const register = useRegister()
  const pow = usePowSolution()
  const navigate = useNavigate()
  const { next, query } = useNext()
  const t = useT()

  const error = register.error instanceof ApiError ? register.error : null
  const powFailed = Boolean(error?.fieldError('pow'))

  const submit = async (retried: boolean) => {
    setChecking(true)
    let solution: PowSolution
    try {
      solution = await pow.take()
    } catch {
      setChecking(false)
      void pow.prepare()
      register.reset()
      return
    } finally {
      setChecking(false)
    }
    register.mutate(
      { username: username.trim(), email: email.trim(), password, pow: solution },
      {
        onSuccess: () => navigate(next, { replace: true }),
        onError: (failure) => {
          const expired = failure instanceof ApiError && failure.code.startsWith('pow_')
          // 题过期或被用过（页面开太久）：换一道题自动再试一次，用户不用知道
          if (expired && !retried) {
            void pow.prepare()
            void submit(true)
            return
          }
          void pow.prepare()
        },
      },
    )
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    void submit(false)
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h1 className="text-3xl font-extrabold">{t('auth.createAccount')}</h1>

      <Field
        label={t('auth.username')}
        name="username"
        autoComplete="username"
        maxLength={15}
        placeholder={t('auth.usernameHint')}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        error={error?.fieldError('username')}
        required
      />
      <Field
        label={t('auth.email')}
        name="email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={error?.fieldError('email')}
        required
      />
      <Field
        label={t('auth.password')}
        name="password"
        type="password"
        autoComplete="new-password"
        placeholder={t('auth.passwordHint')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={error?.fieldError('password') ?? error?.fieldError('_')}
        required
      />

      {error && (!error.details || powFailed) && (
        <p role="alert" className="text-sm text-danger">
          {powFailed ? t('auth.powFailed') : error.message}
        </p>
      )}

      <Button type="submit" size="lg" fullWidth loading={register.isPending || checking}>
        {checking ? t('auth.powChecking') : t('auth.register')}
      </Button>

      <p className="text-center text-[15px] text-ink-muted">
        {t('auth.haveAccount')}{' '}
        <Link to={`/login${query}`} className="text-brand-ink hover:underline">
          {t('auth.login')}
        </Link>
      </p>
    </form>
  )
}
